import { useEffect, useRef, memo, useState } from "react";
import { bool, string, number, func, object, oneOf, oneOfType } from "prop-types";
import { NumericFormat } from "react-number-format";
import { capitalize } from "@material-ui/core";
import { Dialog } from "@components";
import classnames from "classnames";
import useStyle from "./ClearInput.style";
import { NavigationDataKey } from "@tools/EstimateEditor/hooks/useNavigationKeybinds";

export const ClearInputSimple = memo(({
  value,
  onChange,
  onFocus,
  onBlur,
  placeholder,
  placeholderColor,
  borderColor,
  style,
  addInitialBorder,
  disabled,
  className,
  autoFocus,
  showDecimals,
  fixedDecimalScale,
  fullWidth,
  clickDialogMessageContent,
  clickDialogMessageTitle,
  tabIndex,
  americanFormat,
  thousandSeparator,
  applySuffixSpacing,
  placeholderAsCopiedValue,
  prefix="",
  suffix="",
  allowNegative=false,
  confirmOnEnter=true,
  alignContent="center",
  max=9999,
  min=0,
  color="primary",
  size="large",
  decimals=2,
  navigationKey,
}) => {
  const classes = useStyle({ placeholderColor, borderColor });

  const [ localValue, setValue ] = useState(value?.toString());
  const [ focused, setFocused ] = useState(false);
  const [ tooltipVisible, showTooltip ] = useState(false);
  const [ tooltipClosed, setTooltipClosed ] = useState(false);
  const [ fromDblClick, setFromDblClick ] = useState(false);

  const inputRef = useRef();

  const formatterProps = {
    thousandSeparator: thousandSeparator ?? (americanFormat ? "," : " "),
    decimalSeparator: ".",
    allowedDecimalSeparators: [",", "."],
    valueIsNumericString: true,
    allowNegative,
    decimalScale: showDecimals || fixedDecimalScale ? decimals : 0,
    fixedDecimalScale,
    prefix,
    suffix: applySuffixSpacing && Boolean(suffix) ? ` ${suffix.trim()}` : suffix,
    value: localValue,
  }

  useEffect(() => {
    setValue(value?.toString() || "");
  }, [ value, placeholder ]);

  useEffect(() => {
    if(focused && fromDblClick) {
      setTimeout(() => (
        inputRef.current.setSelectionRange(0, 99999)
      ), 100);
      setFromDblClick(false);
    }
  }, [ focused, fromDblClick ]);

  const checkKey = (e) => {
    if ( clickDialogMessageContent ) return;
    const { key } = e;
    if ( confirmOnEnter && key === "Enter" ) {
      e.stopPropagation();
      setFocused(true);
      // blur(e, true);
      inputRef.current.blur();
    }
    if ( focused && key === "Escape" ) {
      e.stopPropagation();
      setFocused(false);
      inputRef.current.blur();
    }
    if(focused && (e.ctrlKey || e.metaKey) && e.key.toUpperCase() === "C") {
      navigator.clipboard.writeText(localValue || (placeholderAsCopiedValue && placeholder ? placeholder : value));
    }
  };

  const handleFocus = ( e, fromDblClick=false ) => {
    if ( tooltipClosed ) {
      setTooltipClosed(false);
      setTimeout(() => (
        inputRef.current?.blur()
      ), 100);
      return;
    }

    if ( clickDialogMessageContent ) {
      e.preventDefault?.();
      showTooltip(true);
      return;
    }
    if ( !focused ) {
      onFocus && onFocus();
      setFocused(true);
      if(!fromDblClick)
        setValue("");
    }
  };

  const handleBlur = ( e, fromEnterKey = false ) => {
    if ( clickDialogMessageContent ) return;
    if ( focused || fromEnterKey ) {
      onBlur && onBlur();
      setFocused(false);
      if ( typeof localValue !== "string" || localValue === "" || localValue === (value?.toString() || "") ) {
        setValue(value?.toString());
        return;
      }
      if ( onChange ) {
        const cancelled = onChange(localValue ? parseFloat(localValue) : undefined);
        if ( cancelled )
          setValue(value?.toString());
      }
    }
  };

  const handleDblClick = ( e ) => {
    if(!focused)
      handleFocus(e, true);
    else if(!localValue)
      setValue(value?.toString());

    setFromDblClick(true);
  }

  const handleTooltipClose = () => {
    showTooltip(false);
    setTooltipClosed(true);
  };

  const onValueChange = ( values ) => {
    if ( clickDialogMessageContent ) return;
    if ( focused )
      setValue(values.floatValue === undefined ? undefined : Math.max(Math.min(max, values.floatValue || 0), min).toString());
  };

  const checkMinMax = ( { floatValue } ) => {
    if ( clickDialogMessageContent ) return false;
    return !(floatValue > max && localValue === max.toString())
      && !(floatValue < min && localValue === min.toString());
  };

  return (
    <>
      <NumericFormat
        getInputRef={ inputRef }
        onValueChange={ onValueChange }
        className={ classnames(
          "noDrag",
          classes.root,
          classes[ alignContent ],
          disabled ?  classes.disabled : classes[ `color${ capitalize(color) }` ],
          disabled && !addInitialBorder && classes.disabledHover,
          fullWidth && classes.fullWidth,
          addInitialBorder && classes.addInitialBorder,
          size === "small" && classes.sizeSmall,
          placeholderColor && classes.colorPlaceholder,
          className,
        ) }
        onFocus={ handleFocus }
        onBlur={ handleBlur }
        onDoubleClick={ handleDblClick }
        onKeyDown={ checkKey }
        isAllowed={ checkMinMax }
        placeholder={ placeholder }
        disabled={ disabled }
        autoFocus={ autoFocus }
        style={ style }
        tabIndex={ tabIndex }
        { ...formatterProps }
        {...{[NavigationDataKey]: clickDialogMessageContent ? undefined : navigationKey}}
      />
      {
        Boolean(clickDialogMessageContent) &&
        <Dialog
          open={ tooltipVisible }
          onClose={ handleTooltipClose }
          title={ clickDialogMessageTitle }
        >
          { clickDialogMessageContent }
        </Dialog>
      }
    </>
  )
});

ClearInputSimple.propTypes = {
  value: oneOfType([ number, string ]),
  onChange: func.isRequired,
  onFocus: func,
  onBlur: func,
  placeholder: oneOfType([ string, number ]),
  placeholderColor: string,
  borderColor: string,
  alignContent: oneOf([ "left", "center", "right" ]),
  style: object,
  max: number,
  min: number,
  confirmOnEnter: bool,
  prefix: string,
  suffix: string,
  disabled: bool,
  className: string,
  autoFocus: bool,
  color: oneOf([ "primary", "text", "warning", "error" ]),
  addInitialBorder: bool,
  showDecimals: bool,
  fixedDecimalScale: bool,
  allowNegative: bool,
  fullWidth: bool,
  americanFormat: bool,
  applySuffixSpacing: bool,
  placeholderAsCopiedValue: bool,
  size: oneOf([ "small", "large" ]),
  clickDialogMessageTitle: string,
  clickDialogMessageContent: string,
  tabIndex: number,
  decimals: number,
  navigationKey: string,
  thousandSeparator: string,
};
